/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';

import Loading from 'components/Loading/Loading';
import DashboardStore from 'stores/DashboardStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import styles from './TradingAccountSelect.module.scss';

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

type State = {
  id: string,
  isExpanded: boolean,
  name: string,
};

@translate()
@inject('dashboardStore')
@observer
export default class TradingAccountSelect extends ComponentWithInjectedProps<
  {},
  State,
  InjectedProps
> {
  constructor(props: any) {
    super(props);
    const { dashboardStore } = this.props;
    const tradingAccountLocalStore: any = localStorage.getItem('dashboardTradingAccount')
      ? JSON.parse(localStorage.getItem('dashboardTradingAccount'))
      : null;
    const activeId: string = tradingAccountLocalStore ? tradingAccountLocalStore.id : 0;
    const activeLabel: string = tradingAccountLocalStore
      ? tradingAccountLocalStore.name
      : 'All Trading Accounts';

    dashboardStore.storeTradingAccount(activeId);

    this.state = {
      id: activeId,
      isExpanded: false,
      name: activeLabel,
    };
  }

  componentDidMount() {
    this.loadTradingAccounts();
  }

  async loadTradingAccounts(): any {
    const dashboardStore = this.props.dashboardStore;
    await dashboardStore.loadAccounts();
  }

  toggleExpanded() {
    this.setState({
      isExpanded: !this.state.isExpanded,
    });
  }

  async setTradingAccount(id: string, label: string) {
    const { dashboardStore } = this.props;
    const tasState: any = {
      id,
      isExpanded: false,
      name: label,
    };

    await dashboardStore.storeTradingAccount(id);

    this.setState(tasState);
    localStorage.setItem('dashboardTradingAccount', JSON.stringify(tasState));
  }

  render() {
    const { dashboardStore, t } = this.props;
    const accounts: any = dashboardStore.myaccounts ? dashboardStore.myaccounts.data : null;

    return (
      <Card className={styles['trading-account-select']}>
        <CardHeader
          className={styles['card-header']}
          title={
            <Typography variant="headline">
              {t('general:actions.select_trading_account')}
            </Typography>
          }
        />
        <Divider />
        <ExpansionPanel
          className={styles['expansion-panel']}
          elevation={0}
          expanded={this.state.isExpanded}
        >
          <ExpansionPanelSummary
            className={styles['tas-header']}
            onClick={e => this.toggleExpanded()}
          >
            {this.state.name === 'All Trading Accounts'
              ? t('general:trading_accounts.labels.all')
              : this.state.name}
            <svg className={styles['expand-icon']}>
              <use
                xlinkHref={`${this.state.isExpanded ? '#icon-chevron-down' : '#icon-chevron-up'}`}
              />
            </svg>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            {dashboardStore.loadingAccounts ? (
              <Loading />
            ) : (
              <List component="nav" className={styles['tas-list']}>
                <ListItem
                  button={true}
                  disabled={this.state.id === 0}
                  onClick={e => this.setTradingAccount(0, 'All Trading Accounts')}
                >
                  <ListItemText primary={t('general:trading_accounts.labels.all')} />
                </ListItem>
                {accounts
                  ? accounts.map((account: any) => {
                      return (
                        <ListItem
                          button={true}
                          key={account.id}
                          disabled={this.state.id === account.id}
                          onClick={e => this.setTradingAccount(account.id, account.name)}
                        >
                          <ListItemText primary={account.name} />
                        </ListItem>
                      );
                    })
                  : null}
              </List>
            )}
          </ExpansionPanelDetails>
        </ExpansionPanel>
      </Card>
    );
  }
}
